import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/api/client";
import { useConfirm } from "@/context/ConfirmContext";
import { useTrackerAccess } from "@/context/TrackerAccessContext";
import { TrackerImg } from "@/components/TrackerPhoto";
import { DatePicker } from "@/components/DatePicker";
import TrackerReportModal from "@/components/TrackerReportModal";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Calendar, PencilSimple, Plus, Trash, X, Camera, FileText,
  ClockCounterClockwise, CheckCircle, Sparkle, Image as ImageIcon, FilePdf, Buildings,
} from "@phosphor-icons/react";
import { useWorkspaceBrand } from "@/context/WorkspaceBrandContext";
import { printProjectSummary } from "@/lib/projectSummaryPdf";
import { SortableList } from "@/components/SortableList";
import { TowersBar, TowerModal } from "@/components/TrackerTowers";

const STATUS_STYLE = {
  "Not Started": "bg-neutral-100 text-neutral-600",
  "In Progress": "bg-blue-50 text-blue-700",
  "On Hold": "bg-amber-50 text-amber-700",
  "Completed": "bg-emerald-50 text-emerald-700",
};

export default function TrackerProjectDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const trackerAccess = useTrackerAccess();
  const [data, setData] = useState(null);
  const [editInfo, setEditInfo] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reportModal, setReportModal] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [addingActivity, setAddingActivity] = useState(false);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [towerId, setTowerId] = useState("");
  const [towerModal, setTowerModal] = useState(null);
  const { workspace } = useWorkspaceBrand();

  const load = async () => {
    try {
      const { data } = await api.get(`/tracker/projects/${id}`);
      setData(data);
      setTowerId((cur) => (data.towers.some((t) => t.id === cur) ? cur : data.towers[0]?.id || ""));
    } catch (err) { toast.error(formatError(err)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  useEffect(() => {
    const rid = searchParams.get("report");
    if (rid) setReportModal({ mode: "view", reportId: rid });
  }, [searchParams]);

  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;
  const { project, client, towers } = data;
  const tower = towers.find((t) => t.id === towerId) || null;
  const inScope = (x) => !towers.length || x.tower_id === towerId;
  const activities = data.activities.filter(inScope);
  const photos = data.photos.filter(inScope);
  const reports = data.reports.filter(inScope);
  const role = trackerAccess?.projects?.find((p) => p.id === id)?.role || (trackerAccess?.isAdmin ? "admin" : null);
  const canEdit = role === "admin" || role === "editor";
  const downloadSummary = async () => {
    setSummaryBusy(true);
    try { await printProjectSummary({ project, client, activities, photos, reports, workspace, towers, tower }); }
    catch (err) { toast.error(formatError(err)); }
    finally { setSummaryBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto fade-in" data-testid="tracker-project-detail">
      <button onClick={() => nav("/tracker")} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 mb-4 transition-colors"><ArrowLeft size={14} /> Back to Projects</button>

      <div className="dos-card p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900" data-testid="tracker-project-name">{project.name}</h1>
              <span className={`dos-badge ${STATUS_STYLE[project.status] || "bg-neutral-100"}`}>{project.status}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500 mt-2">
              {client && <span>{client.name}</span>}
              {project.location && <span className="flex items-center gap-1"><MapPin size={12} /> {project.location}</span>}
              {project.start_date && <span className="flex items-center gap-1"><Calendar size={12} /> Started {project.start_date}</span>}
            </div>
            <div className="text-xs text-neutral-400 mt-1">Last updated: {project.last_activity_update ? new Date(project.last_activity_update).toLocaleString() : "Never"}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="font-display text-4xl font-bold text-neutral-900" data-testid="tracker-overall-percent">{project.overall_percent}%</div>
              <div className="text-xs text-neutral-500">Overall completion</div>
            </div>
            <button data-testid="project-summary-pdf-btn" disabled={summaryBusy} onClick={downloadSummary} className="dos-btn dos-btn-secondary text-xs"><FilePdf size={12} /> {summaryBusy ? "Preparing…" : "Summary PDF"}</button>
            {canEdit && <button data-testid="add-tower-btn" onClick={() => setTowerModal({ mode: "add" })} className="dos-btn dos-btn-secondary text-xs"><Buildings size={12} /> Add Tower / Wing</button>}
            {canEdit && <button data-testid="edit-project-info-btn" onClick={() => setEditInfo(true)} className="dos-btn dos-btn-secondary text-xs"><PencilSimple size={12} /> Edit</button>}
          </div>
        </div>
        <div className="h-2 bg-neutral-100 rounded-full overflow-hidden mt-4">
          <div className="h-full bg-neutral-900 rounded-full transition-all duration-500" style={{ width: `${project.overall_percent}%` }} />
        </div>
      </div>

      {towers.length > 0 && <TowersBar towers={towers} selected={towerId} onSelect={setTowerId} canEdit={canEdit} onEdit={(t) => setTowerModal({ mode: "edit", tower: t })} />}

      <ActivitiesSection project={project} tower={tower} activities={activities} canEdit={canEdit} onChanged={load} onHistory={setHistoryFor} adding={addingActivity} setAdding={setAddingActivity} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <PhotosSection projectId={id} tower={tower} photos={photos} activities={activities} canEdit={canEdit} onChanged={load} onUpload={() => setUploadOpen(true)} onOpen={setLightbox} />
        <ReportsSection projectId={id} tower={tower} reports={reports} canEdit={canEdit} onGenerate={() => setReportModal({ mode: "generate" })} onOpen={(rid) => setReportModal({ mode: "view", reportId: rid })} />
      </div>

      {editInfo && <EditProjectModal project={project} isAdmin={role === "admin"} onClose={() => setEditInfo(false)} onSaved={() => { setEditInfo(false); load(); }} />}
      {towerModal && <TowerModal {...towerModal} projectId={id} hasTowers={towers.length > 0} isAdmin={role === "admin"} onClose={() => setTowerModal(null)} onSaved={(newId) => { setTowerModal(null); if (newId) setTowerId(newId); load(); }} />}
      {uploadOpen && <UploadPhotoModal projectId={id} towerId={towerId} activities={activities} onClose={() => setUploadOpen(false)} onSaved={() => { setUploadOpen(false); load(); }} />}
      {lightbox && <PhotoLightbox photo={lightbox} canEdit={canEdit} onClose={() => setLightbox(null)} onDeleted={() => { setLightbox(null); load(); }} />}
      {historyFor && <HistoryModal activity={historyFor} onClose={() => setHistoryFor(null)} />}
      {reportModal && (
        <TrackerReportModal
          mode={reportModal.mode}
          reportId={reportModal.reportId}
          project={project}
          tower={tower}
          activities={activities}
          photos={photos}
          canEdit={canEdit}
          onClose={() => setReportModal(null)}
          onSaved={() => { setReportModal(null); load(); }}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ================= Activities =================
function ActivitiesSection({ project, tower, activities, canEdit, onChanged, onHistory, adding, setAdding }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ percent: 0, remark: "" });
  const confirm = useConfirm();

  const startEdit = (a) => { setEditingId(a.id); setDraft({ percent: a.completion_percent, remark: "" }); };
  const saveProgress = async (a) => {
    try {
      await api.post(`/tracker/activities/${a.id}/progress`, draft);
      toast.success("Progress updated");
      setEditingId(null);
      onChanged();
    } catch (err) { toast.error(formatError(err)); }
  };
  const toggleApplicable = async (a) => {
    try { await api.patch(`/tracker/activities/${a.id}`, { is_applicable: !a.is_applicable }); onChanged(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const removeActivity = async (a) => {
    const ok = await confirm({ title: `Remove "${a.name}"?`, message: "Its progress history will also be deleted.", confirmLabel: "Remove", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/tracker/activities/${a.id}`); toast.success("Removed"); onChanged(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const [ordered, setOrdered] = useState(activities);
  useEffect(() => { setOrdered(activities); }, [activities]);
  const reorder = async (next) => {
    setOrdered(next);
    try { await api.post(`/tracker/projects/${project.id}/activities/reorder`, { order: next.map((a) => a.id) }); onChanged(); }
    catch (err) { setOrdered(activities); toast.error(formatError(err)); }
  };

  return (
    <div className="dos-card overflow-hidden" data-testid="tracker-activities-section">
      <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500" data-testid="activities-title">Activity Checklist{tower ? ` — ${tower.name}` : ""}</div>
        {canEdit && <button data-testid="add-activity-btn" onClick={() => setAdding(true)} className="dos-btn dos-btn-secondary text-xs"><Plus size={12} /> Add Activity</button>}
      </div>
      <SortableList items={ordered} getId={(a) => a.id} onReorder={reorder} disabled={!canEdit} renderItem={(a, idx, handle) => (
          <div className={`px-5 py-3 border-b border-neutral-100 ${!a.is_applicable ? "opacity-40" : ""}`} data-testid={`activity-row-${a.id}`}>
            <div className="flex items-center gap-3">
              {handle}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-neutral-900">{a.name}</span>
                  <span className={`dos-badge text-[10px] ${STATUS_STYLE[a.status] || "bg-neutral-100"}`}>{a.status}</span>
                  {a.assigned_to && <span className="text-[11px] text-neutral-400">· {a.assigned_to}</span>}
                  {!a.is_applicable && <span className="text-[11px] text-neutral-400">· Not Applicable</span>}
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 max-w-xs h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-neutral-900 rounded-full" style={{ width: `${a.completion_percent}%` }} /></div>
                  <span className="text-xs font-mono text-neutral-600 w-10">{a.completion_percent}%</span>
                </div>
                {a.latest_remark && <div className="text-xs text-neutral-500 mt-1 italic">"{a.latest_remark}"</div>}
                <div className="text-[10px] text-neutral-400 mt-0.5">{a.last_updated_by_name ? `Updated by ${a.last_updated_by_name} · ` : ""}{a.last_updated_at ? new Date(a.last_updated_at).toLocaleDateString() : "Not updated yet"}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => onHistory(a)} title="History" data-testid={`history-btn-${a.id}`} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-400"><ClockCounterClockwise size={14} /></button>
                {canEdit && (
                  <>
                    <button onClick={() => startEdit(a)} title="Update progress" data-testid={`update-progress-btn-${a.id}`} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-400"><PencilSimple size={14} /></button>
                    <label className="p-1.5 rounded hover:bg-neutral-100 text-neutral-400 cursor-pointer" title="Not Applicable" data-testid={`not-applicable-toggle-${a.id}`}>
                      <input type="checkbox" className="hidden" checked={!a.is_applicable} onChange={() => toggleApplicable(a)} />
                      <CheckCircle size={14} weight={!a.is_applicable ? "fill" : "regular"} />
                    </label>
                    <button onClick={() => removeActivity(a)} data-testid={`remove-activity-${a.id}`} className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
                  </>
                )}
              </div>
            </div>
            {editingId === a.id && (
              <div className="mt-3 bg-neutral-50 rounded-md p-3 flex flex-wrap items-center gap-3" data-testid={`progress-editor-${a.id}`}>
                <input type="range" min="0" max="100" value={draft.percent} onChange={(e) => setDraft({ ...draft, percent: parseInt(e.target.value, 10) })} className="flex-1 min-w-[140px]" data-testid={`progress-slider-${a.id}`} />
                <input type="number" min="0" max="100" value={draft.percent} onChange={(e) => setDraft({ ...draft, percent: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) })} className="dos-input w-20 py-1" data-testid={`progress-number-${a.id}`} />
                <input placeholder="Remark (optional)" value={draft.remark} onChange={(e) => setDraft({ ...draft, remark: e.target.value })} className="dos-input flex-1 min-w-[160px] py-1" data-testid={`progress-remark-${a.id}`} />
                <button onClick={() => saveProgress(a)} data-testid={`progress-save-${a.id}`} className="dos-btn dos-btn-primary text-xs">Save</button>
                <button onClick={() => setEditingId(null)} className="dos-btn dos-btn-secondary text-xs">Cancel</button>
              </div>
            )}
          </div>
        )} />
      {activities.length === 0 && <div className="p-8 text-center text-neutral-500 text-sm">No activities yet.</div>}
      {adding && <AddActivityModal projectId={project.id} towerId={tower?.id || ""} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); onChanged(); }} />}
    </div>
  );
}

function AddActivityModal({ projectId, towerId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [weight, setWeight] = useState(1);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/tracker/projects/${projectId}/activities`, { name, weight: parseFloat(weight) || 1, tower_id: towerId }); toast.success("Activity added"); onSaved(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-sm fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold">Add Activity</div>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input data-testid="new-activity-name" required autoFocus placeholder="Activity name" value={name} onChange={(e) => setName(e.target.value)} className="dos-input" />
          <div><label className="text-xs text-neutral-500 mb-1 block">Weight (for overall % — default 1 = equal weight)</label><input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="dos-input" /></div>
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="new-activity-save" type="submit" className="dos-btn dos-btn-primary">Add</button>
        </div>
      </form>
    </div>
  );
}

function HistoryModal({ activity, onClose }) {
  const [history, setHistory] = useState([]);
  useEffect(() => { api.get(`/tracker/activities/${activity.id}/history`).then((r) => setHistory(r.data)); }, [activity.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-md max-h-[80vh] overflow-y-auto fade-in" data-testid="activity-history-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white">
          <div className="font-display font-semibold">{activity.name} — History</div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {history.map((h) => (
            <div key={h.id} className="border-l-2 border-neutral-200 pl-3">
              <div className="text-sm font-medium">{h.percent}% <span className="text-xs text-neutral-400 font-normal">by {h.updated_by_name}</span></div>
              {h.remark && <div className="text-xs text-neutral-500 italic">"{h.remark}"</div>}
              <div className="text-[10px] text-neutral-400">{new Date(h.created_at).toLocaleString()}</div>
            </div>
          ))}
          {history.length === 0 && <div className="text-sm text-neutral-500 text-center py-4">No updates recorded yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ================= Project info edit =================
function EditProjectModal({ project, isAdmin, onClose, onSaved }) {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: project.name, client_id: project.client_id || "", location: project.location || "", start_date: project.start_date || "" });
  const [onHold, setOnHold] = useState(project.status === "On Hold");
  const [archived, setArchived] = useState(!!project.is_archived);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/tracker/projects/${project.id}`, { ...form, status: onHold ? "On Hold" : "In Progress", is_archived: archived });
      toast.success(archived ? "Project updated and archived" : "Project updated"); onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };
  const del = async () => {
    const ok = await confirm({ title: `Delete "${project.name}"?`, message: "This permanently deletes all activities, photos, site visit reports and history for this project. This cannot be undone.", confirmLabel: "Delete Project", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/tracker/projects/${project.id}`);
      toast.success("Project deleted");
      nav("/tracker?tab=projects");
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-md fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold">Edit Project</div>
          <button type="button" data-testid="edit-project-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input required data-testid="edit-project-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dos-input" />
          <select data-testid="edit-project-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="dos-input">
            <option value="">— No client linked —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input placeholder="Location" data-testid="edit-project-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="dos-input" />
          <div><label className="text-xs text-neutral-500 mb-1 block">Start Date</label><DatePicker testId="edit-project-start-date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
          <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer pt-1">
            <input type="checkbox" data-testid="edit-project-on-hold-toggle" checked={onHold} onChange={(e) => setOnHold(e.target.checked)} />
            Park this project On Hold
          </label>
          <div className="text-[11px] text-neutral-400">When not On Hold, status is auto-calculated from activity progress (Not Started / In Progress / Completed).</div>
          {isAdmin && (
            <div className="pt-3 mt-3 border-t border-neutral-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-2">Danger Zone</div>
              <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                <input type="checkbox" data-testid="edit-project-archive-toggle" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
                Archive this project
              </label>
              <div className="text-[11px] text-neutral-400 mt-1 mb-3">Hides it from everyone's Dashboard and Projects list. Nothing is deleted — untick to bring it back any time.</div>
              <button type="button" data-testid="delete-project-btn" onClick={del} className="dos-btn text-xs bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 w-full justify-center"><Trash size={12} /> Delete Project Permanently</button>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" data-testid="edit-project-cancel" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button type="submit" data-testid="edit-project-save" className="dos-btn dos-btn-primary">Save</button>
        </div>
      </form>
    </div>
  );
}

// ================= Photos =================
function groupByDate(photos) {
  const map = {};
  photos.forEach((p) => { const d = (p.visit_date || p.created_at || "").slice(0, 10); (map[d] = map[d] || []).push(p); });
  return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
}

function PhotosSection({ projectId, tower, photos, activities, canEdit, onChanged, onUpload, onOpen }) {
  const grouped = groupByDate(photos);
  return (
    <div className="dos-card overflow-hidden" data-testid="tracker-photos-section">
      <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5"><Camera size={14} /> Site Photographs{tower ? ` — ${tower.name}` : ""}</div>
        {canEdit && <button data-testid="upload-photo-btn" onClick={onUpload} className="dos-btn dos-btn-secondary text-xs"><Plus size={12} /> Upload</button>}
      </div>
      <div className="p-4 max-h-[420px] overflow-y-auto">
        {grouped.map(([date, items]) => (
          <div key={date} className="mb-4">
            <div className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">{new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {items.map((p) => (
                <button key={p.id} onClick={() => onOpen(p)} data-testid={`photo-thumb-${p.id}`} className="aspect-square rounded-md overflow-hidden bg-neutral-100 hover:opacity-80 transition-opacity">
                  <TrackerImg photoId={p.id} className="w-full h-full object-cover" alt={p.caption} />
                </button>
              ))}
            </div>
          </div>
        ))}
        {photos.length === 0 && <div className="text-center text-neutral-500 text-sm py-8">No photos uploaded yet.</div>}
      </div>
    </div>
  );
}

function UploadPhotoModal({ projectId, towerId, activities, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ activity_id: "", caption: "", observation: "", visit_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a photo"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("tower_id", towerId || "");
      await api.post(`/tracker/projects/${projectId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-md fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold">Upload Site Photograph</div>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input data-testid="photo-file-input" required type="file" accept="image/*" capture="environment" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          <select value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} className="dos-input">
            <option value="">General site update (no specific activity)</option>
            {activities.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <input placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="dos-input" />
          <textarea placeholder="Observation (optional)" value={form.observation} onChange={(e) => setForm({ ...form, observation: e.target.value })} className="dos-input" rows={2} />
          <div><label className="text-xs text-neutral-500 mb-1 block">Date</label><DatePicker testId="upload-photo-date" value={form.visit_date} onChange={(v) => setForm({ ...form, visit_date: v })} /></div>
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="photo-upload-save" type="submit" disabled={saving} className="dos-btn dos-btn-primary">{saving ? "Uploading…" : "Upload"}</button>
        </div>
      </form>
    </div>
  );
}

function PhotoLightbox({ photo, canEdit, onClose, onDeleted }) {
  const confirm = useConfirm();
  const del = async () => {
    const ok = await confirm({ title: "Delete this photo?", message: "This cannot be undone.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/tracker/photos/${photo.id}`); toast.success("Deleted"); onDeleted(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-lightbox">
      <div onClick={(e) => e.stopPropagation()} className="max-w-2xl w-full">
        <TrackerImg photoId={photo.id} className="w-full max-h-[70vh] object-contain rounded-md bg-black" alt={photo.caption} />
        <div className="bg-white rounded-b-md p-4 flex items-start justify-between gap-4">
          <div>
            {photo.caption && <div className="font-medium text-sm text-neutral-900">{photo.caption}</div>}
            {photo.observation && <div className="text-xs text-neutral-500 mt-1">{photo.observation}</div>}
            <div className="text-[11px] text-neutral-400 mt-1">{photo.visit_date} · {photo.uploaded_by_name}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canEdit && <button data-testid="lightbox-delete-btn" onClick={del} className="dos-btn dos-btn-secondary text-xs"><Trash size={12} /></button>}
            <button onClick={onClose} className="dos-btn dos-btn-secondary text-xs"><X size={12} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ================= Reports =================
function ReportsSection({ projectId, tower, reports, canEdit, onGenerate, onOpen }) {
  return (
    <div className="dos-card overflow-hidden" data-testid="tracker-reports-section">
      <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5"><FileText size={14} /> Site Visit Reports{tower ? ` — ${tower.name}` : ""}</div>
        {canEdit && <button data-testid="generate-report-btn" onClick={onGenerate} className="dos-btn dos-btn-primary text-xs"><Sparkle size={12} /> Generate Report</button>}
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {reports.map((r) => (
          <button key={r.id} onClick={() => onOpen(r.id)} data-testid={`report-item-${r.id}`} className="w-full text-left px-5 py-3 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-neutral-900">Visit on {r.visit_date}</div>
              <div className="text-xs text-neutral-400">by {r.generated_by_name} · v{r.version}</div>
            </div>
            <span className={`dos-badge ${r.status === "finalized" ? "bg-emerald-50 text-emerald-700" : r.status === "pending_review" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-600"}`}>{r.status === "pending_review" ? "Pending Review" : r.status === "finalized" ? "Finalised" : "Draft"}</span>
          </button>
        ))}
        {reports.length === 0 && <div className="text-center text-neutral-500 text-sm py-8">No site visit reports yet.</div>}
      </div>
    </div>
  );
}
